import { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Paper, Grid, Card, CardContent,
  Button, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, IconButton, Tooltip, Alert, CircularProgress
} from '@mui/material';
import {
  AccountBalance, TrendingDown, Warning, CheckCircle,
  Visibility, Payment, Receipt, Refresh
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

function CuentasPorPagar() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resumenObligaciones, setResumenObligaciones] = useState(null);
  const [proveedoresConSaldo, setProveedoresConSaldo] = useState([]);
  const [comprasPendientes, setComprasPendientes] = useState([]);

  const getConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => { cargarDatos(); }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      setError(null);
      const config = getConfig();

      const [resumenRes, proveedoresRes, comprasRes] = await Promise.all([
        axios.get(`${API_URL}/api/cuentas-pagar/resumen-obligaciones`, config).catch(() => ({ data: { data: null } })),
        axios.get(`${API_URL}/api/cuentas-pagar/proveedores-con-saldo`, config).catch(() => ({ data: { data: [] } })),
        axios.get(`${API_URL}/api/cuentas-pagar/compras-pendientes`, config).catch(() => ({ data: { data: [] } }))
      ]);

      setResumenObligaciones(resumenRes.data.data);
      setProveedoresConSaldo(proveedoresRes.data.data || []);
      setComprasPendientes(comprasRes.data.data || []);
    } catch (err) {
      console.error('Error al cargar datos:', err);
      setError(err.response?.data?.message || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const fmt = (v) => `$${parseFloat(v || 0).toFixed(2)}`;

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px"><CircularProgress /></Box>;

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={700}>Cuentas por Pagar</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={cargarDatos}>Actualizar</Button>
          <Button variant="contained" startIcon={<Payment />}
            onClick={() => navigate('/cuentas-pagar/registrar-pago')}>Registrar Pago</Button>
          <Button variant="outlined" color="secondary"
            onClick={() => navigate('/cuentas-pagar/pagos')}>Ver Pagos</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Tarjetas Resumen */}
      {resumenObligaciones && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={6} sm={3}>
            <Card><CardContent sx={{ py: 1.5 }}>
              <Typography variant="caption" color="text.secondary">Total Comprado</Typography>
              <Typography variant="h6">{fmt(resumenObligaciones.total_comprado)}</Typography>
              <Typography variant="caption">{resumenObligaciones.cantidad_compras} compras</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card><CardContent sx={{ py: 1.5 }}>
              <Typography variant="caption" color="text.secondary">Total Pagado</Typography>
              <Typography variant="h6" color="success.main">{fmt(resumenObligaciones.total_pagado)}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: '#fff3e0' }}><CardContent sx={{ py: 1.5 }}>
              <Typography variant="caption" color="text.secondary">Saldo Pendiente</Typography>
              <Typography variant="h6" color="error.main">{fmt(resumenObligaciones.saldo_pendiente)}</Typography>
              <Typography variant="caption">{resumenObligaciones.compras_pendientes} pendientes</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card><CardContent sx={{ py: 1.5 }}>
              <Typography variant="caption" color="text.secondary">Vencido +90d</Typography>
              <Typography variant="h6" color="warning.main">{fmt(resumenObligaciones.antiguedad_saldos?.['90+'])}</Typography>
            </CardContent></Card>
          </Grid>
        </Grid>
      )}

      {/* DESGLOSE: Facturas Pendientes de Pago */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, bgcolor: '#e65100', color: '#fff', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Receipt />
          <Typography variant="h6" fontWeight={700}>Facturas Pendientes de Pago</Typography>
          <Chip label={comprasPendientes.length} size="small" sx={{ bgcolor: '#fff', color: '#e65100', fontWeight: 700, ml: 1 }} />
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5', '& th': { fontWeight: 700, fontSize: '0.75rem' } }}>
                <TableCell>Fecha</TableCell>
                <TableCell>Proveedor</TableCell>
                <TableCell>RUC</TableCell>
                <TableCell>No. Factura</TableCell>
                <TableCell align="right">Total Factura</TableCell>
                <TableCell align="right">Pagado</TableCell>
                <TableCell align="right">Saldo Pendiente</TableCell>
                <TableCell>Crédito</TableCell>
                <TableCell>Vencimiento</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {comprasPendientes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No hay facturas pendientes de pago</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                comprasPendientes.map((compra, idx) => {
                  const total = parseFloat(compra.totales?.importeTotal || compra.total || 0);
                  const saldo = parseFloat(compra.saldo_pendiente ?? total);
                  const pagado = total - saldo;
                  const diasCredito = compra.condiciones_pago?.dias_credito || 0;
                  const fechaVenc = compra.condiciones_pago?.fecha_vencimiento;
                  const hoy = new Date();
                  const vencida = fechaVenc && new Date(fechaVenc) < hoy;
                  const provRuc = compra.proveedor?.ruc || '';

                  return (
                    <TableRow key={compra.id} hover sx={{
                      bgcolor: idx % 2 === 0 ? '#fff' : '#fafafa',
                      '& td': { fontSize: '0.78rem', py: 0.8 }
                    }}>
                      <TableCell>{compra.fecha_emision || new Date(compra.created_at).toLocaleDateString('es-EC')}</TableCell>
                      <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {compra.proveedor?.razonSocial || compra.proveedor?.razon_social || '—'}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>{provRuc}</TableCell>
                      <TableCell>{compra.factura?.numeroComprobante || '—'}</TableCell>
                      <TableCell align="right">{fmt(total)}</TableCell>
                      <TableCell align="right" sx={{ color: 'success.main' }}>{fmt(pagado)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'error.main' }}>{fmt(saldo)}</TableCell>
                      <TableCell>
                        {diasCredito > 0 ? (
                          <Chip label={`${diasCredito}d`} color="warning" size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                        ) : (
                          <Chip label="Contado" size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.72rem' }}>
                        {fechaVenc ? (
                          <span style={{ color: vencida ? '#005eff' : 'inherit', fontWeight: vencida ? 700 : 400 }}>
                            {fechaVenc}
                          </span>
                        ) : '—'}
                      </TableCell>
                      <TableCell>
                        {vencida ? (
                          <Chip label="Vencida" color="error" size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                        ) : pagado > 0 ? (
                          <Chip label="Parcial" color="warning" size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                        ) : (
                          <Chip label="Pendiente" color="info" size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Registrar Pago">
                          <IconButton size="small" color="success"
                            onClick={() => navigate(`/cuentas-pagar/registrar-pago?proveedorId=${provRuc}`)}>
                            <Payment fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {comprasPendientes.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, bgcolor: '#fff3e0', borderTop: '2px solid #e65100' }}>
            <Typography variant="body2" fontWeight={700}>{comprasPendientes.length} factura(s) pendiente(s)</Typography>
            <Typography variant="body2" fontWeight={700} color="error.main">
              Total Pendiente: {fmt(comprasPendientes.reduce((s, c) => s + parseFloat(c.saldo_pendiente ?? c.totales?.importeTotal ?? 0), 0))}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Proveedores con Saldo */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Proveedores con Saldo Pendiente</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell><strong>Proveedor</strong></TableCell>
                <TableCell><strong>RUC</strong></TableCell>
                <TableCell align="center"><strong>Facturas Pend.</strong></TableCell>
                <TableCell align="right"><strong>Saldo Total</strong></TableCell>
                <TableCell align="center"><strong>Acciones</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!proveedoresConSaldo || proveedoresConSaldo.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">No hay proveedores con saldo pendiente</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                proveedoresConSaldo.map((p) => (
                  <TableRow key={p.proveedor_id} hover>
                    <TableCell>{p.proveedor_nombre}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{p.proveedor_ruc}</TableCell>
                    <TableCell align="center">
                      <Chip label={p.compras_pendientes} size="small" color="error" />
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="error.main">{fmt(p.saldo_total)}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Ver Estado de Cuenta">
                        <IconButton size="small" color="primary"
                          onClick={() => navigate(`/cuentas-pagar/estado-cuenta/${p.proveedor_id}`)}>
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Registrar Pago">
                        <IconButton size="small" color="success"
                          onClick={() => navigate(`/cuentas-pagar/registrar-pago?proveedorId=${p.proveedor_id}`)}>
                          <Payment />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
}

export default CuentasPorPagar;
